import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchRoadSnapCandidates, type RoadSnapCandidateDto } from '../../services/api';

const RESTRICTION_TYPES = [
  { id: 'no_left_turn', label: 'No left turn' },
  { id: 'no_right_turn', label: 'No right turn' },
  { id: 'no_u_turn', label: 'No U-turn' },
  { id: 'no_straight_on', label: 'No straight through' },
] as const;

export type TurnRestrictionTypeId = (typeof RESTRICTION_TYPES)[number]['id'];

export type TurnRestrictionRoadPick = {
  roadId: number;
  name: string;
  endpoint: string;
};

type Props = {
  fromRoad: TurnRestrictionRoadPick | null;
  onFromRoadChange: (road: TurnRestrictionRoadPick | null) => void;
  toRoad: TurnRestrictionRoadPick | null;
  onToRoadChange: (road: TurnRestrictionRoadPick | null) => void;
  restrictionType: TurnRestrictionTypeId | '';
  onRestrictionTypeChange: (id: TurnRestrictionTypeId) => void;
  proximity: { lat: number; lng: number } | null;
  labelClass: string;
};

function candidateLabel(c: RoadSnapCandidateDto): string {
  if (c.source === 'contribution' || c.contribution_id) {
    const name = c.name?.trim() || 'Your pending survey';
    return `${name} (${c.endpoint} · pending)`;
  }
  const name = c.name?.trim() || (c.road_id != null ? `Road ${c.road_id}` : 'Road');
  return `${name} (${c.endpoint})`;
}

function RoadPickList({
  title,
  candidates,
  selectedId,
  onSelect,
}: {
  title: string;
  candidates: RoadSnapCandidateDto[];
  selectedId: number | null;
  onSelect: (pick: TurnRestrictionRoadPick | null) => void;
}) {
  if (candidates.length === 0) {
    return <p className="text-xs text-gray-500 dark:text-neutral-400">No road endpoints in range — move the pin closer.</p>;
  }
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-gray-700 dark:text-neutral-300">{title}</p>
      <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
        {candidates.filter((c): c is RoadSnapCandidateDto & { road_id: number } => c.road_id != null).map((c) => {
          const roadId = c.road_id;
          const active = selectedId === roadId;
          return (
            <button
              key={`${roadId}-${c.endpoint}`}
              type="button"
              onClick={() =>
                onSelect(
                  active
                    ? null
                    : {
                        roadId,
                        name: c.name?.trim() || `Road ${roadId}`,
                        endpoint: c.endpoint,
                      },
                )
              }
              className={`rounded-lg border px-2.5 py-2 text-left text-xs transition-colors ${
                active
                  ? 'border-violet-600 bg-violet-600 text-white'
                  : 'border-gray-200 bg-white text-gray-800 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100'
              }`}
            >
              {candidateLabel(c)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TurnRestrictionSurveyFields({
  fromRoad,
  onFromRoadChange,
  toRoad,
  onToRoadChange,
  restrictionType,
  onRestrictionTypeChange,
  proximity,
  labelClass,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<RoadSnapCandidateDto[]>([]);

  useEffect(() => {
    if (!proximity) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchRoadSnapCandidates(proximity.lng, proximity.lat, 25, { includeMyPending: false })
      .then((rows) => {
        if (!cancelled) setCandidates(rows.filter((r) => r.eligible !== false));
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proximity?.lat, proximity?.lng]);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-violet-200/80 bg-violet-50 px-3 py-2.5 text-xs text-violet-950 dark:border-violet-800/60 dark:bg-violet-950/30 dark:text-violet-100">
        <p className="font-semibold">Turn restriction (not one-way)</p>
        <p className="mt-1">
          Place the pin at the intersection, then pick the incoming road and the road where the turn
          is banned.
        </p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading roads near pin…
        </div>
      ) : (
        <>
          <RoadPickList
            title="1. You are on (from road)"
            candidates={candidates}
            selectedId={fromRoad?.roadId ?? null}
            onSelect={onFromRoadChange}
          />
          <RoadPickList
            title="2. Cannot turn onto (to road)"
            candidates={candidates.filter((c) => c.road_id !== fromRoad?.roadId)}
            selectedId={toRoad?.roadId ?? null}
            onSelect={onToRoadChange}
          />
        </>
      )}
      <div>
        <p className={labelClass}>3. Rule</p>
        <div className="flex flex-wrap gap-2">
          {RESTRICTION_TYPES.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onRestrictionTypeChange(opt.id)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                restrictionType === opt.id
                  ? 'border-violet-600 bg-violet-600 text-white'
                  : 'border-gray-300 bg-white text-gray-800 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
